"use client";

/**
 * The tour page's section navigation, minus the chrome that draws it.
 *
 * Extracted from `_tour-tabs-bar.client.tsx` on 2026-09-16, when the mobile
 * bottom tab bar became a second consumer. It lived in that component file and
 * was imported from it, which worked until a spec mocked the module for the
 * BAR and the hook disappeared with it — a component file is a thing tests
 * replace wholesale, so it is the wrong home for logic two components share.
 */

import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { pickActiveSection } from "./_active-section";
import type { TourTab } from "./_section-order";

/** Used only before layout can be measured (SSR, or a click in the same tick
 *  the bar mounts). A hardcoded offset is exactly what let the scroll target
 *  and the scrollspy marker drift apart, so everything else measures. */
const FALLBACK_BAR = 52;

/**
 * The line a section must cross to count as "current" — the bottom edge of the
 * pinned chrome, so a section becomes active as its heading clears the bar.
 *
 * ONE source of truth, used by both the click-scroll target and the scrollspy.
 * They used to disagree: sections carry `scroll-mt-32` (128px) while the
 * document also declares `scroll-padding-top: 72px`, and the two ADD — every
 * section landed at 200px instead of 128px. That 72px gap left the previous
 * section's tail inside the observed band, which is what made the underline
 * lag one tab behind. Deriving both from here means they cannot drift again.
 *
 * READ OFF THE BAR ITSELF — its resolved `top` plus its height — rather than
 * measured from the header. Where the bar comes to rest IS the marker, so this
 * cannot disagree with the CSS whatever that CSS turns out to be.
 *
 * It used to add `document.querySelector("header")`'s height, which assumed one
 * header. There are two. The standard `<Nav>` is `fixed` and does owe its 64px;
 * an APPLIED header from the Header & Footer Builder renders IN FLOW
 * (`site-chrome.tsx` § `mainSpacing`) and scrolls away, owing nothing. On those
 * storefronts the old sum counted a header that was no longer on screen —
 * measured on staging at scroll 2200 the applied header sat at y=-2156 and was
 * still being added — so every landing fell 64px short of the section its tab
 * promised.
 */
export function markerOffset(nav: HTMLElement | null): number {
  // `closest`, not `parentElement`. The bar gained an inner flex row when the
  // Share control moved in beside the tabs, so the nav's parent is no longer
  // the sticky box — and the parent's computed `top` is then `auto`, which
  // parses to NaN and silently drops the whole 64px header offset. Every tab
  // click would have landed 64px short.
  //
  // The document-wide lookup is the fallback because THE MARKER IS A PROPERTY
  // OF THE PAGE, not of whichever nav is asking. The bottom tab bar is `fixed`
  // at the other end of the viewport and is inside no sticky box at all, so
  // `closest` finds nothing for it — and answering `FALLBACK_BAR` there gave it
  // a 52px marker against the 113px the top strip and every scroll target use.
  // It read the section ABOVE the one it had just scrolled to, so tapping
  // "Dates" scrolled correctly and then lit "Overview".
  const bar =
    nav?.closest<HTMLElement>("[data-tour-tab-bar]") ??
    document.querySelector<HTMLElement>("[data-tour-tab-bar]");
  if (!bar) return FALLBACK_BAR;
  const stickyTop = Number.parseFloat(getComputedStyle(bar).top);
  return Math.round(
    (Number.isFinite(stickyTop) ? stickyTop : 0) +
      bar.getBoundingClientRect().height,
  );
}

/** True when the page cannot scroll further — the final section is often too
 *  short to ever reach the marker on its own. */
function atPageBottom(): boolean {
  const doc = document.documentElement;
  return window.innerHeight + window.scrollY >= doc.scrollHeight - 2;
}

/**
 * Scrollspy: which section the reader is currently in.
 *
 * `IntersectionObserver` rather than a scroll listener because this app holds a
 * hard INP budget — a per-frame scroll handler is the classic way to blow it.
 * The observer is only a CHEAP TRIGGER: every callback recomputes the answer
 * from live geometry via `pickActiveSection`, so the result never depends on
 * which entries happened to change, how many tabs exist, or the order an
 * operator arranged them in.
 *
 * `lockUntil` suppresses the observer during a click-driven scroll. The click
 * sets the active tab itself; without the lock the observer would fire at
 * intermediate positions mid-animation and overwrite that choice with whatever
 * was passing by.
 *
 * The lock is now released by the scroll ENDING (`endClickScroll`), not by a
 * timer running out. It was 900ms, which is neither the duration of a smooth
 * scroll nor anything else the browser guarantees; on a long page the animation
 * outlived it and the observer overwrote the click's choice halfway down. The
 * timestamp survives only as a backstop, for the case where the settle signal
 * never arrives at all — without it the spy would be frozen for the rest of the
 * visit.
 */
const CLICK_LOCK_BACKSTOP_MS = 6000;

/**
 * EXPORTED for the bottom tab bar, which needs the same answer for its own
 * (different) list of destinations — it carries "Dates", which is not an
 * operator tab, and drops any section the operator switched off.
 *
 * Two observers rather than one shared context: each is a cheap trigger over a
 * handful of section elements, and the alternative is a provider that has to
 * reconcile two different slug lists and two different click locks to save
 * about a millisecond of work per scroll. `pickActiveSection` is pure, so both
 * bars derive their answer from the same geometry either way.
 */
export function useActiveSection(
  tabs: TourTab[],
  navRef: RefObject<HTMLElement | null>,
): {
  activeSlug: string | null;
  selectOnClick: (slug: string) => void;
  endClickScroll: () => void;
} {
  const [activeSlug, setActiveSlug] = useState<string | null>(
    tabs[0]?.slug ?? null,
  );
  const lockUntil = useRef(0);

  const recompute = useCallback(() => {
    if (Date.now() < lockUntil.current) return;
    const positions = tabs
      .map((tab) => document.getElementById(tab.slug))
      .filter((el): el is HTMLElement => el != null)
      .map((el) => ({ slug: el.id, top: el.getBoundingClientRect().top }));
    const next = pickActiveSection(
      positions,
      markerOffset(navRef.current),
      atPageBottom(),
    );
    if (next) setActiveSlug(next);
  }, [tabs, navRef]);

  /** Called by a tab click: take the choice immediately, and hold the observer
   *  off until the scroll has settled. */
  const selectOnClick = useCallback((slug: string) => {
    setActiveSlug(slug);
    lockUntil.current = Date.now() + CLICK_LOCK_BACKSTOP_MS;
  }, []);

  /** Called once the click's scroll has stopped and been corrected. Re-reads
   *  live geometry rather than trusting what the click asserted: the landing
   *  may be short of the section, and the observer will not fire again until
   *  some section edge next crosses the band — which, inside a tall section, is
   *  a long way off. */
  const endClickScroll = useCallback(() => {
    lockUntil.current = 0;
    recompute();
  }, [recompute]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const sections = tabs
      .map((tab) => document.getElementById(tab.slug))
      .filter((el): el is HTMLElement => el != null);
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(() => recompute(), {
      // A band, not a hairline: sections are separated by gaps, and a 1px
      // marker can fall inside one and fire nothing at all.
      //
      // Its top edge is MEASURED, like everything else here. It was a hardcoded
      // `-116px`, which happens to be the desktop chrome (64px header + 52px
      // bar) and is 12px wrong on a phone, where the tab row is taller. That
      // only shifts when the observer fires — `recompute` re-measures and
      // answers from live geometry either way — but this file's whole premise
      // is that a hardcoded offset is what lets the two drift apart.
      //
      // Measured once, at mount, not on resize: the band is a trigger, and a
      // trigger that is a few pixels stale costs nothing, whereas rebuilding
      // the observer on every resize frame would cost real work.
      rootMargin: `-${markerOffset(navRef.current)}px 0px -55% 0px`,
      threshold: 0,
    });
    sections.forEach((section) => observer.observe(section));
    recompute();
    return () => observer.disconnect();
  }, [tabs, recompute]);

  return { activeSlug, selectOnClick, endClickScroll };
}
